"use client";

import * as React from 'react';
import { cn } from '../lib/utils';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Accessible name when the label is an icon or abbreviation. */
  ariaLabel?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string>
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
  options: ReadonlyArray<SegmentedControlOption<T>>;
  value: T;
  onValueChange: (value: T) => void;
  /** Names the set, e.g. "Period". */
  'aria-label': string;
  size?: 'default' | 'sm';
}

/**
 * One choice from a few, applied at once (a period, a scope). Each option is
 * a toggle button with aria-pressed; the chosen one takes the selection tint.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  size = 'default',
  className,
  ...props
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      className={cn('inline-flex max-w-full flex-wrap items-center gap-1', className)}
      {...props}
    >
      {options.map((option) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            aria-label={option.ariaLabel}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors duration-[var(--duration-fast)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
              'disabled:pointer-events-none disabled:text-disabled',
              size === 'sm' ? 'h-7 px-2 text-xs' : 'h-8 px-2.5 text-sm pointer-coarse:h-11',
              pressed
                ? 'bg-selected font-semibold text-selected-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
